import React, { useState } from 'react';
import { Package, User, Phone, Mail, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GetQuoteSection = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    serviceType: '',
    origin: '',
    destination: '',
    cargoDetails: '',
    estimatedDate: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/quotes/request`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          companyName: '',
          contactPerson: '',
          phone: '',
          email: '',
          serviceType: '',
          origin: '',
          destination: '',
          cargoDetails: '',
          estimatedDate: ''
        });
      }
    } catch (error) {
      console.error('Quote request error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="get-quote" className="relative flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden py-20">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
        <h1 className="text-[5rem] md:text-[12rem] font-bold text-white select-none">
          PRIMESWIFT
        </h1>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <div className="inline-block mb-4 px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full">
              <span className="text-blue-300 text-sm font-semibold">GET STARTED</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Request a Custom Quote
            </h2>
            <p className="text-base md:text-xl text-gray-300 mb-8 leading-relaxed">
              Get competitive rates tailored to your logistics needs. Our team will respond within 2 hours with a detailed quote.
            </p>

            {/* Process Steps */}
            <div className="space-y-6">
              {[
                { step: '01', title: 'Submit Your Requirements', desc: 'Fill in the quote form with your logistics needs' },
                { step: '02', title: 'Get Instant Response', desc: 'Our team reviews and responds within 2 hours' },
                { step: '03', title: 'Confirm & Ship', desc: 'Accept the quote and we handle the rest' }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div className="mt-12 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl">
              <h4 className="font-bold mb-4">Need immediate assistance?</h4>
              <div className="space-y-2 text-gray-300">
                <p>📞 Call us: +91-9579027425</p>
                <p>📧 Email: info@primeswift.in</p>
                <p>⏰ Available 24/7</p>
              </div>
            </div>
          </div>

          {/* Quote Form */}
          <Card className="shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center">
                <Package className="mr-2 h-6 w-6 text-blue-600" />
                Get Your Quote
              </CardTitle>
              <CardDescription>
                Fill in the details and we'll get back to you with the best rates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company name"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contactPerson">
                      <User className="inline h-4 w-4 mr-1" />
                      Contact Person *
                    </Label>
                    <Input
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@company.com"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="serviceType">Service Required *</Label>
                  <Input
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    placeholder="e.g., Freight, Warehousing, Last-mile"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="origin">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Origin *
                    </Label>
                    <Input
                      id="origin"
                      name="origin"
                      value={formData.origin}
                      onChange={handleChange}
                      placeholder="From city"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="destination">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Destination *
                    </Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      placeholder="To city"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="estimatedDate">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Estimated Shipping Date
                  </Label>
                  <Input
                    id="estimatedDate"
                    name="estimatedDate"
                    type="date"
                    value={formData.estimatedDate}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="cargoDetails">Cargo Details *</Label>
                  <Textarea
                    id="cargoDetails"
                    name="cargoDetails"
                    value={formData.cargoDetails}
                    onChange={handleChange}
                    placeholder="Weight, dimensions, type of goods, special requirements..."
                    rows={3}
                    required
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg group"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Get Quote Now'}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
